import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { 
  Trophy, Users, Ticket, CreditCard, Plus, 
  Loader2, Check, X, Download, RefreshCw, Gift, ArrowLeft,
  Trash2, Pencil, Upload, Shuffle
} from 'lucide-react';
import { Progress } from '../components/ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { format } from 'date-fns';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [stats, setStats] = useState(null);
  const [raffles, setRaffles] = useState([]);
  const [users, setUsers] = useState([]);
  const [payments, setPayments] = useState([]);
  const [rounds, setRounds] = useState([]);
  const [roundEntries, setRoundEntries] = useState([]);
  const [selectedRound, setSelectedRound] = useState(null);
  const [loading, setLoading] = useState(true);

  // Dialog states
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingRaffle, setEditingRaffle] = useState(null);
  
  // Form states - separate for better control
  const [title, setTitle] = useState('');
  const [prizeAmount, setPrizeAmount] = useState('');
  const [totalTickets, setTotalTickets] = useState('');
  const [ticketPrice, setTicketPrice] = useState('');
  const [drawDate, setDrawDate] = useState(null);
  const [imageUrl, setImageUrl] = useState('');
  const [imagePreview, setImagePreview] = useState('');
  
  const [submitting, setSubmitting] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (user?.role !== 'admin') {
      navigate('/');
      return;
    }
    fetchData();
  }, [user, navigate]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchData = async () => {
    setLoading(true);
    try {
      const [statsRes, rafflesRes, usersRes, paymentsRes, roundsRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/stats`),
        axios.get(`${API_URL}/api/raffles`),
        axios.get(`${API_URL}/api/admin/users`),
        axios.get(`${API_URL}/api/admin/payments`),
        axios.get(`${API_URL}/api/admin/rounds`)
      ]);
      
      setStats(statsRes.data);
      setRaffles(rafflesRes.data);
      setUsers(usersRes.data.users);
      setPayments(paymentsRes.data.payments);
      setRounds(roundsRes.data);
    } catch (error) {
      console.error('Error fetching admin data:', error);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setTitle('');
    setPrizeAmount('');
    setTotalTickets('');
    setTicketPrice('');
    setDrawDate(null);
    setImageUrl('');
    setImagePreview('');
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
        setImageUrl(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const openCreateDialog = () => {
    resetForm();
    setShowCreateDialog(true);
  };

  const openEditDialog = (raffle) => {
    setEditingRaffle(raffle);
    setTitle(raffle.title);
    setPrizeAmount(raffle.prize_amount.toString());
    setTotalTickets(raffle.total_tickets.toString());
    setTicketPrice(raffle.ticket_price.toString());
    setDrawDate(new Date(raffle.draw_date));
    setImageUrl(raffle.image_url || '');
    setImagePreview(raffle.image_url || '');
    setShowEditDialog(true);
  };

  const handleCreateRaffle = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      await axios.post(
        `${API_URL}/api/admin/raffles`,
        {
          title,
          prize_amount: parseFloat(prizeAmount),
          total_tickets: parseInt(totalTickets),
          ticket_price: parseFloat(ticketPrice),
          draw_date: drawDate?.toISOString(),
          image_url: imageUrl
        }
      );
      
      setShowCreateDialog(false);
      resetForm();
      fetchData();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to create free draw');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditRaffle = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      await axios.put(
        `${API_URL}/api/admin/raffles/${editingRaffle.raffle_id}`,
        {
          title,
          prize_amount: parseFloat(prizeAmount),
          ticket_price: parseFloat(ticketPrice),
          draw_date: drawDate?.toISOString(),
          image_url: imageUrl
        },
        
      );
      
      setShowEditDialog(false);
      setEditingRaffle(null);
      resetForm();
      fetchData();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to update free draw');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteRaffle = async (raffleId) => {
    if (!window.confirm('Are you sure you want to delete this free draw? This cannot be undone.')) return;
    
    try {
      await axios.delete(`${API_URL}/api/admin/raffles/${raffleId}`);
      fetchData();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to delete free draw');
    }
  };

  const handleApprovePayment = async (paymentId) => {
    try {
      await axios.post(`${API_URL}/api/admin/payments/${paymentId}/approve`, {});
      fetchData();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to approve payment');
    }
  };

  const handleRejectPayment = async (paymentId) => {
    try {
      await axios.post(`${API_URL}/api/admin/payments/${paymentId}/reject`, {});
      fetchData();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to reject payment');
    }
  };

  const handleSyncStripe = async () => {
    try {
      const response = await axios.post(`${API_URL}/api/admin/payments/sync-stripe`, {});
      alert(`Synced! ${response.data.confirmed} confirmed, ${response.data.expired} expired`);
      fetchData();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to sync with Stripe');
    }
  };

  // Round management functions
  const handleCloseRound = async (roundId) => {
    if (!window.confirm('Close this round? No more entries will be accepted.')) return;
    try {
      await axios.post(`${API_URL}/api/admin/rounds/${roundId}/close`, {});
      fetchData();
    } catch (error) { alert(error.response?.data?.detail || 'Failed'); }
  };

  const handleDrawRound = async (roundId) => {
    if (!window.confirm('Draw a winner for this round? This cannot be undone.')) return;
    try {
      const res = await axios.post(`${API_URL}/api/admin/rounds/${roundId}/draw`, {});
      alert(`${res.data.message}\nWinner: ${res.data.winner.name} (${res.data.winner.entry_type})`);
      fetchData();
    } catch (error) { alert(error.response?.data?.detail || 'Failed'); }
  };

  const handleClaimPrize = async (roundId) => {
    try {
      await axios.post(`${API_URL}/api/admin/rounds/${roundId}/claim`, {});
      fetchData();
    } catch (error) { alert(error.response?.data?.detail || 'Failed'); }
  };

  const handleViewEntries = async (roundId) => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/rounds/${roundId}/entries`);
      setRoundEntries(res.data);
      setSelectedRound(roundId);
    } catch (error) { alert('Failed to load entries'); }
  };

  const handleRunDraw = async (raffleId) => {
    if (!window.confirm('Are you sure you want to run the draw? This cannot be undone.')) return;
    
    try {
      const response = await axios.post(`${API_URL}/api/admin/raffles/${raffleId}/draw`, {});
      alert(`Winner: Ticket #${response.data.winner_ticket}`);
      fetchData();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to run draw');
    }
  };

  const handleExportTickets = async (raffleId) => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/export/tickets/${raffleId}`);
      if (response.data.total === 0) {
        alert('No tickets with owners to export yet');
        return;
      }
      const blob = new Blob([response.data.csv], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `tickets-${raffleId}.csv`;
      a.click();
    } catch (error) {
      alert('Failed to export tickets');
    }
  };

  const handleRandomizeNumbers = async (raffleId) => {
    if (!window.confirm('Randomize all ticket numbers? This will reassign random numbers to all tickets.')) return;
    
    try {
      const response = await axios.post(`${API_URL}/api/admin/raffles/${raffleId}/randomize-numbers`, {});
      alert(response.data.message);
      fetchData();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to randomize numbers');
    }
  };

  const handleExportUsers = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/export/users`);
      const blob = new Blob([response.data.csv], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'users.csv';
      a.click();
    } catch (error) {
      alert('Failed to export users');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#EAB308] animate-spin" />
      </div>
    );
  }

  const pendingPayments = payments.filter(p => p.status === 'pending');

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-white/80 border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors" data-testid="back-to-home">
              <ArrowLeft className="w-5 h-5" />
              <span>Back to Site</span>
            </Link>
            <div className="flex items-center gap-2">
              <img src="/logo.png" alt="Boss Win" className="h-20 w-auto" />
              <span className="font-heading text-xl font-black text-gray-900">Admin</span>
            </div>
            <button
              onClick={logout}
              className="text-gray-600 hover:text-red-500 transition-colors text-sm"
            >
              Logout
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm">
            <div className="flex items-center gap-3 mb-2">
              <Users className="w-5 h-5 text-[#EAB308]" />
              <span className="text-gray-500 text-sm">Total Users</span>
            </div>
            <p className="text-2xl font-bold text-gray-900" data-testid="stat-users">{stats?.total_users || 0}</p>
          </div>
          
          <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm">
            <div className="flex items-center gap-3 mb-2">
              <Gift className="w-5 h-5 text-[#EAB308]" />
              <span className="text-gray-500 text-sm">Active Free Draws</span>
            </div>
            <p className="text-2xl font-bold text-gray-900" data-testid="stat-raffles">{stats?.active_raffles || 0}</p>
          </div>
          
          <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm">
            <div className="flex items-center gap-3 mb-2">
              <CreditCard className="w-5 h-5 text-yellow-500" />
              <span className="text-gray-500 text-sm">Pending Payments</span>
            </div>
            <p className="text-2xl font-bold text-gray-900" data-testid="stat-pending">{stats?.pending_payments || 0}</p>
          </div>
          
          <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm">
            <div className="flex items-center gap-3 mb-2">
              <CreditCard className="w-5 h-5 text-green-500" />
              <span className="text-gray-500 text-sm">Total Revenue</span>
            </div>
            <p className="text-2xl font-bold text-gray-900" data-testid="stat-revenue">£{(stats?.total_revenue || 0).toLocaleString()}</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
          {[
            { id: 'overview', label: 'Free Draws', icon: Gift },
            { id: 'rounds', label: 'Rounds', icon: RefreshCw },
            { id: 'payments', label: 'Payments', icon: CreditCard },
            { id: 'users', label: 'Users', icon: Users }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-xl font-medium transition-colors whitespace-nowrap ${
                activeTab === tab.id 
                  ? 'bg-[#EAB308] text-white' 
                  : 'bg-white text-gray-600 hover:text-gray-900 border border-gray-200'
              }`}
              data-testid={`tab-${tab.id}`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
              {tab.id === 'payments' && pendingPayments.length > 0 && (
                <span className="bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">
                  {pendingPayments.length}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Create Raffle Dialog */}
        <Dialog open={showCreateDialog} onOpenChange={(open) => { setShowCreateDialog(open); if (!open) resetForm(); }}>
          <DialogContent className="bg-white border-gray-200 text-gray-900 max-w-md max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl">Create New Free Draw</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreateRaffle} className="space-y-4 mt-4">
              <div>
                <label className="block text-sm text-gray-600 mb-1">Title</label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 text-gray-900 focus:border-[#EAB308] focus:outline-none"
                  placeholder="BMW X3 M40i + £15,000"
                  required
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-600 mb-1">Prize (£)</label>
                  <input
                    type="number"
                    value={prizeAmount}
                    onChange={(e) => setPrizeAmount(e.target.value)}
                    className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 text-gray-900 focus:border-[#EAB308] focus:outline-none"
                    placeholder="50000"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-600 mb-1">Ticket Price (£)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={ticketPrice}
                    onChange={(e) => setTicketPrice(e.target.value)}
                    className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 text-gray-900 focus:border-[#EAB308] focus:outline-none"
                    placeholder="5.00"
                    required
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm text-gray-600 mb-1">Total Tickets</label>
                <input
                  type="number"
                  value={totalTickets}
                  onChange={(e) => setTotalTickets(e.target.value)}
                  className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 text-gray-900 focus:border-[#EAB308] focus:outline-none"
                  placeholder="10000"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm text-gray-600 mb-1">Draw Date</label>
                <Popover>
                  <PopoverTrigger asChild>
                    <button
                      type="button"
                      className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 text-left text-gray-900 focus:border-[#EAB308] focus:outline-none"
                    >
                      {drawDate ? format(drawDate, 'PPP') : 'Select date'}
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0 bg-white border-gray-200 z-[100]" align="start">
                    <Calendar
                      mode="single"
                      selected={drawDate}
                      onSelect={setDrawDate}
                      disabled={(date) => date < new Date()}
                      className="bg-white"
                    />
                  </PopoverContent>
                </Popover>
              </div>
              
              {/* Image Upload */}
              <div>
                <label className="block text-sm text-gray-600 mb-1">Free Draw Image</label>
                <div className="space-y-3">
                  {imagePreview ? (
                    <div className="relative">
                      <img 
                        src={imagePreview} 
                        alt="Preview" 
                        className="w-full h-40 object-cover rounded-xl"
                      />
                      <button
                        type="button"
                        onClick={() => { setImagePreview(''); setImageUrl(''); }}
                        className="absolute top-2 right-2 bg-red-500 text-white p-1 rounded-full hover:bg-red-600"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ) : (
                    <div 
                      onClick={() => fileInputRef.current?.click()}
                      className="w-full h-40 border-2 border-dashed border-gray-300 rounded-xl flex flex-col items-center justify-center cursor-pointer hover:border-[#EAB308] transition-colors"
                    >
                      <Upload className="w-8 h-8 text-gray-400 mb-2" />
                      <span className="text-sm text-gray-500">Click to upload image</span>
                    </div>
                  )}
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="hidden"
                  />
                  <div className="text-center text-gray-400 text-sm">or</div>
                  <input
                    type="url"
                    value={imageUrl}
                    onChange={(e) => { setImageUrl(e.target.value); setImagePreview(e.target.value); }}
                    className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-2 text-gray-900 focus:border-[#EAB308] focus:outline-none text-sm"
                    placeholder="Paste image URL here..."
                  />
                </div>
              </div>
              
              <button
                type="submit"
                disabled={submitting || !drawDate}
                className="w-full bg-[#EAB308] text-white font-bold py-3 rounded-xl hover:bg-[#CA8A04] transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : <Plus className="w-5 h-5" />}
                Create Free Draw
              </button>
            </form>
          </DialogContent>
        </Dialog>

        {/* Edit Raffle Dialog */}
        <Dialog open={showEditDialog} onOpenChange={(open) => { setShowEditDialog(open); if (!open) { setEditingRaffle(null); resetForm(); } }}>
          <DialogContent className="bg-white border-gray-200 text-gray-900 max-w-md max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl">Edit Free Draw</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleEditRaffle} className="space-y-4 mt-4">
              <div>
                <label className="block text-sm text-gray-600 mb-1">Title</label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 text-gray-900 focus:border-[#EAB308] focus:outline-none"
                  placeholder="BMW X3 M40i + £15,000"
                  required
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-600 mb-1">Prize (£)</label>
                  <input
                    type="number"
                    value={prizeAmount}
                    onChange={(e) => setPrizeAmount(e.target.value)}
                    className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 text-gray-900 focus:border-[#EAB308] focus:outline-none"
                    placeholder="50000"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-600 mb-1">Ticket Price (£)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={ticketPrice}
                    onChange={(e) => setTicketPrice(e.target.value)}
                    className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 text-gray-900 focus:border-[#EAB308] focus:outline-none"
                    placeholder="5.00"
                    required
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm text-gray-600 mb-1">Total Tickets</label>
                <input
                  type="number"
                  value={totalTickets}
                  className="w-full bg-gray-100 border border-gray-200 rounded-xl px-4 py-3 text-gray-500 cursor-not-allowed"
                  disabled
                />
                <p className="text-xs text-gray-400 mt-1">Cannot change total tickets after creation</p>
              </div>
              
              <div>
                <label className="block text-sm text-gray-600 mb-1">Draw Date</label>
                <Popover>
                  <PopoverTrigger asChild>
                    <button
                      type="button"
                      className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 text-left text-gray-900 focus:border-[#EAB308] focus:outline-none"
                    >
                      {drawDate ? format(drawDate, 'PPP') : 'Select date'}
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0 bg-white border-gray-200 z-[100]" align="start">
                    <Calendar
                      mode="single"
                      selected={drawDate}
                      onSelect={setDrawDate}
                      disabled={(date) => date < new Date()}
                      className="bg-white"
                    />
                  </PopoverContent>
                </Popover>
              </div>
              
              {/* Image Upload */}
              <div>
                <label className="block text-sm text-gray-600 mb-1">Free Draw Image</label>
                <div className="space-y-3">
                  {imagePreview ? (
                    <div className="relative">
                      <img 
                        src={imagePreview} 
                        alt="Preview" 
                        className="w-full h-40 object-cover rounded-xl"
                      />
                      <button
                        type="button"
                        onClick={() => { setImagePreview(''); setImageUrl(''); }}
                        className="absolute top-2 right-2 bg-red-500 text-white p-1 rounded-full hover:bg-red-600"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ) : (
                    <div 
                      onClick={() => fileInputRef.current?.click()}
                      className="w-full h-40 border-2 border-dashed border-gray-300 rounded-xl flex flex-col items-center justify-center cursor-pointer hover:border-[#EAB308] transition-colors"
                    >
                      <Upload className="w-8 h-8 text-gray-400 mb-2" />
                      <span className="text-sm text-gray-500">Click to upload image</span>
                    </div>
                  )}
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="hidden"
                  />
                  <div className="text-center text-gray-400 text-sm">or</div>
                  <input
                    type="url"
                    value={imageUrl}
                    onChange={(e) => { setImageUrl(e.target.value); setImagePreview(e.target.value); }}
                    className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-2 text-gray-900 focus:border-[#EAB308] focus:outline-none text-sm"
                    placeholder="Paste image URL here..."
                  />
                </div>
              </div>
              
              <button
                type="submit"
                disabled={submitting || !drawDate}
                className="w-full bg-[#EAB308] text-white font-bold py-3 rounded-xl hover:bg-[#CA8A04] transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : <Pencil className="w-5 h-5" />}
                Update Free Draw
              </button>
            </form>
          </DialogContent>
        </Dialog>

        {/* Tab Content */}
        {activeTab === 'overview' && (
          <div>
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading text-2xl font-bold text-gray-900">Free Draws</h2>
              <button 
                onClick={openCreateDialog}
                className="flex items-center gap-2 bg-[#EAB308] text-white font-bold px-4 py-2 rounded-xl hover:bg-[#CA8A04] transition-colors"
                data-testid="create-raffle-button"
              >
                <Plus className="w-5 h-5" />
                Create Free Draw
              </button>
            </div>

            <div className="space-y-4" data-testid="raffles-list">
              {raffles.map(raffle => (
                <div 
                  key={raffle.raffle_id}
                  className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm"
                >
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="flex gap-4 flex-1">
                      {raffle.image_url && (
                        <img src={raffle.image_url} alt={raffle.title} className="w-20 h-20 object-cover rounded-xl hidden sm:block" />
                      )}
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="font-heading text-lg font-bold text-gray-900">{raffle.title}</h3>
                          {raffle.status === 'active' ? (
                            <span className="bg-green-100 text-green-700 px-2 py-0.5 rounded text-xs font-medium">Active</span>
                          ) : raffle.status === 'drawn' ? (
                            <span className="bg-yellow-100 text-yellow-700 px-2 py-0.5 rounded text-xs font-medium">Drawn #{raffle.winner_ticket}</span>
                          ) : (
                            <span className="bg-gray-100 text-gray-600 px-2 py-0.5 rounded text-xs font-medium">Closed</span>
                          )}
                        </div>
                        <div className="flex flex-wrap gap-4 text-sm text-gray-600">
                          <span>Prize: <strong className="text-[#EAB308]">£{raffle.prize_amount.toLocaleString()}</strong></span>
                          <span>Tickets: {raffle.tickets_sold}/{raffle.total_tickets}</span>
                          <span>Price: £{raffle.ticket_price}</span>
                        </div>
                        <Progress value={(raffle.tickets_sold / raffle.total_tickets) * 100} className="h-2 mt-3 bg-gray-100" />
                      </div>
                    </div>
                    
                    <div className="flex gap-2 flex-wrap">
                      <button
                        onClick={() => openEditDialog(raffle)}
                        className="flex items-center gap-1 bg-blue-50 text-blue-600 px-3 py-2 rounded-lg hover:bg-blue-100 transition-colors text-sm"
                        data-testid={`edit-raffle-${raffle.raffle_id}`}
                      >
                        <Pencil className="w-4 h-4" />
                        Edit
                      </button>
                      <button
                        onClick={() => handleDeleteRaffle(raffle.raffle_id)}
                        className="flex items-center gap-1 bg-red-50 text-red-600 px-3 py-2 rounded-lg hover:bg-red-100 transition-colors text-sm"
                        data-testid={`delete-raffle-${raffle.raffle_id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                        Delete
                      </button>
                      <button
                        onClick={() => handleExportTickets(raffle.raffle_id)}
                        className="flex items-center gap-1 bg-gray-50 border border-gray-200 text-gray-600 px-3 py-2 rounded-lg hover:bg-gray-100 transition-colors text-sm"
                        data-testid={`export-tickets-${raffle.raffle_id}`}
                      >
                        <Download className="w-4 h-4" />
                        Export
                      </button>
                      {raffle.status === 'active' && (
                        <button
                          onClick={() => handleRandomizeNumbers(raffle.raffle_id)}
                          className="flex items-center gap-1 bg-purple-50 text-purple-600 px-3 py-2 rounded-lg hover:bg-purple-100 transition-colors text-sm"
                          data-testid={`randomize-${raffle.raffle_id}`}
                        >
                          <Shuffle className="w-4 h-4" />
                          Randomize
                        </button>
                      )}
                      {raffle.status === 'active' && (
                        <button
                          onClick={() => handleRunDraw(raffle.raffle_id)}
                          className="flex items-center gap-1 bg-[#EAB308] text-white px-3 py-2 rounded-lg hover:bg-[#CA8A04] transition-colors text-sm font-bold"
                          data-testid={`run-draw-${raffle.raffle_id}`}
                        >
                          <Gift className="w-4 h-4" />
                          Run Draw
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Rounds Tab */}
        {activeTab === 'rounds' && (
          <div>
            <h2 className="font-heading text-2xl font-bold text-gray-900 mb-6">Draw Rounds</h2>
            
            {/* Entries Modal */}
            {selectedRound && (
              <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setSelectedRound(null)}>
                <div className="bg-white rounded-2xl p-6 max-w-2xl w-full max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-bold">Round Entries ({roundEntries.length})</h3>
                    <button onClick={() => setSelectedRound(null)} className="text-gray-500 hover:text-gray-900"><X className="w-5 h-5" /></button>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead><tr className="border-b text-left text-gray-500"><th className="py-2 pr-4">#</th><th className="py-2 pr-4">Name</th><th className="py-2 pr-4">Email</th><th className="py-2 pr-4">Type</th><th className="py-2">Date</th></tr></thead>
                      <tbody>
                        {roundEntries.map((e, i) => (
                          <tr key={e.entry_id} className="border-b border-gray-100">
                            <td className="py-2 pr-4 font-mono">#{e.ticket_number}</td>
                            <td className="py-2 pr-4">{e.user_name}</td>
                            <td className="py-2 pr-4 text-gray-500">{e.user_email}</td>
                            <td className="py-2 pr-4"><span className={`px-2 py-0.5 rounded-full text-xs font-medium ${e.entry_type === 'paid' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'}`}>{e.entry_type}</span></td>
                            <td className="py-2 text-gray-400 text-xs">{new Date(e.created_at).toLocaleDateString('en-GB')}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {roundEntries.length === 0 && <p className="text-center text-gray-400 py-6">No entries yet</p>}
                  </div>
                </div>
              </div>
            )}
            
            <div className="space-y-4">
              {rounds.map(round => (
                <div key={round.round_id} className={`bg-white border-2 rounded-2xl p-6 ${round.status === 'active' ? 'border-green-300' : round.status === 'drawn' ? 'border-[#EAB308]/30' : 'border-gray-200'}`}>
                  <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                    <div>
                      <div className="flex items-center gap-3 mb-1">
                        <h3 className="text-lg font-bold text-gray-900">Round #{round.round_number}</h3>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-bold uppercase ${
                          round.status === 'active' ? 'bg-green-100 text-green-700' :
                          round.status === 'closed' ? 'bg-yellow-100 text-yellow-700' :
                          'bg-[#EAB308]/10 text-[#EAB308]'
                        }`}>{round.status}</span>
                      </div>
                      <p className="text-gray-500 text-sm">
                        {new Date(round.start_date).toLocaleDateString('en-GB')} — {new Date(round.close_date).toLocaleDateString('en-GB')}
                        &nbsp;•&nbsp;{round.total_entries} entries
                      </p>
                      {round.winner && (
                        <div className="mt-2 p-3 bg-yellow-50 rounded-lg">
                          <p className="text-sm"><span className="font-bold text-[#EAB308]">Winner:</span> {round.winner.user_name} ({round.winner.user_email})</p>
                          <p className="text-xs text-gray-500">Entry #{round.winner.ticket_number} • {round.winner.entry_type} • {round.winner.prize_claimed ? '✅ Claimed' : `⏳ Claim by ${new Date(round.winner.claim_deadline).toLocaleDateString('en-GB')}`}</p>
                        </div>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <button onClick={() => handleViewEntries(round.round_id)} className="px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm">
                        Entries
                      </button>
                      {round.status === 'active' && (
                        <button onClick={() => handleCloseRound(round.round_id)} className="px-3 py-2 bg-yellow-100 text-yellow-700 rounded-lg hover:bg-yellow-200 transition-colors text-sm font-medium">
                          Close Round
                        </button>
                      )}
                      {(round.status === 'active' || round.status === 'closed') && (
                        <button onClick={() => handleDrawRound(round.round_id)} className="px-3 py-2 bg-[#EAB308] text-white rounded-lg hover:bg-[#CA8A04] transition-colors text-sm font-bold">
                          Draw Winner
                        </button>
                      )}
                      {round.status === 'drawn' && !round.winner?.prize_claimed && (
                        <button onClick={() => handleClaimPrize(round.round_id)} className="px-3 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors text-sm font-bold">
                          Mark Claimed
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
              {rounds.length === 0 && (
                <p className="text-center text-gray-400 py-8">No rounds yet. They will be created automatically.</p>
              )}
            </div>
          </div>
        )}

        {activeTab === 'payments' && (
          <div>
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading text-2xl font-bold text-gray-900">Payments</h2>
              <div className="flex items-center gap-3">
                <button
                  onClick={handleSyncStripe}
                  className="flex items-center gap-2 bg-purple-50 text-purple-600 px-4 py-2 rounded-lg hover:bg-purple-100 transition-colors font-medium"
                  data-testid="sync-stripe-button"
                >
                  <RefreshCw className="w-4 h-4" />
                  Sync Stripe
                </button>
                <button
                  onClick={fetchData}
                  className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
                >
                  <RefreshCw className="w-4 h-4" />
                  Refresh
                </button>
              </div>
            </div>

            {/* Pending Payments */}
            {pendingPayments.length > 0 && (
              <div className="mb-8">
                <h3 className="text-lg font-bold text-yellow-600 mb-4">Pending ({pendingPayments.length})</h3>
                <div className="space-y-4" data-testid="pending-payments">
                  {pendingPayments.map(payment => (
                    <div 
                      key={payment.payment_id}
                      className="bg-white border-2 border-yellow-200 rounded-2xl p-6"
                    >
                      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                        <div>
                          <p className="text-gray-900 font-medium">{payment.user?.full_name || payment.user?.email}</p>
                          <p className="text-gray-500 text-sm">{payment.user?.email} • {payment.user?.phone}</p>
                          <div className="flex gap-4 mt-2 text-sm">
                            <span className="text-[#EAB308] font-bold">£{payment.amount.toFixed(2)}</span>
                            <span className="text-gray-500">{payment.ticket_ids.length} ticket(s)</span>
                            <span className="text-purple-500 font-medium">{payment.payment_method}</span>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          {payment.payment_method !== 'stripe' && (
                            <>
                              <button
                                onClick={() => handleRejectPayment(payment.payment_id)}
                                className="flex items-center gap-1 bg-red-50 text-red-600 px-4 py-2 rounded-lg hover:bg-red-100 transition-colors"
                                data-testid={`reject-payment-${payment.payment_id}`}
                              >
                                <X className="w-4 h-4" />
                                Reject
                              </button>
                              <button
                                onClick={() => handleApprovePayment(payment.payment_id)}
                                className="flex items-center gap-1 bg-green-500 text-white px-4 py-2 rounded-lg hover:bg-green-600 transition-colors font-bold"
                                data-testid={`approve-payment-${payment.payment_id}`}
                              >
                                <Check className="w-4 h-4" />
                                Approve
                              </button>
                            </>
                          )}
                          {payment.payment_method === 'stripe' && (
                            <span className="text-sm text-gray-500 italic">Auto-confirms via Stripe</span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* All Payments */}
            <h3 className="text-lg font-bold text-gray-900 mb-4">All Payments</h3>
            <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden shadow-sm">
              <div className="overflow-x-auto">
                <table className="w-full" data-testid="payments-table">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="text-left text-gray-600 text-sm font-medium p-4">User</th>
                      <th className="text-left text-gray-600 text-sm font-medium p-4">Amount</th>
                      <th className="text-left text-gray-600 text-sm font-medium p-4">Tickets</th>
                      <th className="text-left text-gray-600 text-sm font-medium p-4">Status</th>
                      <th className="text-left text-gray-600 text-sm font-medium p-4">Date</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {payments.map(payment => (
                      <tr key={payment.payment_id} className="hover:bg-gray-50">
                        <td className="p-4">
                          <p className="text-gray-900">{payment.user?.full_name || 'Unknown'}</p>
                          <p className="text-gray-500 text-sm">{payment.user?.email}</p>
                        </td>
                        <td className="p-4 text-gray-900 font-medium">£{payment.amount.toFixed(2)}</td>
                        <td className="p-4 text-gray-600">{payment.ticket_ids.length}</td>
                        <td className="p-4">
                          {payment.status === 'confirmed' ? (
                            <span className="text-green-700 bg-green-100 px-2 py-1 rounded text-sm font-medium">Confirmed</span>
                          ) : payment.status === 'pending' ? (
                            <span className="text-yellow-700 bg-yellow-100 px-2 py-1 rounded text-sm font-medium">Pending</span>
                          ) : (
                            <span className="text-red-700 bg-red-100 px-2 py-1 rounded text-sm font-medium">Rejected</span>
                          )}
                        </td>
                        <td className="p-4 text-gray-500 text-sm">
                          {new Date(payment.created_at).toLocaleDateString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'users' && (
          <div>
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading text-2xl font-bold text-gray-900">Users ({users.length})</h2>
              <button
                onClick={handleExportUsers}
                className="flex items-center gap-2 bg-white border border-gray-200 text-gray-600 px-4 py-2 rounded-xl hover:bg-gray-50 transition-colors"
                data-testid="export-users-button"
              >
                <Download className="w-4 h-4" />
                Export CSV
              </button>
            </div>

            <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden shadow-sm">
              <div className="overflow-x-auto">
                <table className="w-full" data-testid="users-table">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="text-left text-gray-600 text-sm font-medium p-4">Name</th>
                      <th className="text-left text-gray-600 text-sm font-medium p-4">Email</th>
                      <th className="text-left text-gray-600 text-sm font-medium p-4">Phone</th>
                      <th className="text-left text-gray-600 text-sm font-medium p-4">Role</th>
                      <th className="text-left text-gray-600 text-sm font-medium p-4">Joined</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {users.map(u => (
                      <tr key={u.user_id} className="hover:bg-gray-50">
                        <td className="p-4 text-gray-900">{u.full_name || '-'}</td>
                        <td className="p-4 text-gray-600">{u.email}</td>
                        <td className="p-4 text-gray-600">{u.phone || '-'}</td>
                        <td className="p-4">
                          {u.role === 'admin' ? (
                            <span className="text-[#EAB308] bg-yellow-50 px-2 py-1 rounded text-sm font-medium">Admin</span>
                          ) : (
                            <span className="text-gray-600">User</span>
                          )}
                        </td>
                        <td className="p-4 text-gray-500 text-sm">
                          {new Date(u.created_at).toLocaleDateString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
